import { Component, inject, OnInit } from '@angular/core';
import { ReminderSettingTypeEnum } from '@models/reminders/reminder.enum';
import { ReminderSettingModel } from "@models/settings/reminder-setting-model";
import { ReminderSettingsData } from "@models/settings/reminder-settings-data";
import { ReminderSettingsModelExtended } from "@models/settings/reminder-settings-model-extended";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { somethingWentWrong } from "@shared/constants/common-error-messages";
import { ReminderNameTypePipe } from "@shared/pipes/reminderNameTypePipe";
import { IPartialHeaderData } from "@shared/services/header.service";
import { SettingsApiService } from '@shared/services/settings-api.service';
import { CellClickedEvent, ColDef, GridApi, RowSelectionOptions, ValueFormatterParams } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { take } from "rxjs";
import {
  AgReminderSendingMethodLabelComponent
} from "./components/ag-reminder-sending-method-label/ag-reminder-sending-method-label.component";
import {
  AgReminderSettingsActionComponentComponent
} from './components/ag-reminder-settings-action-component/ag-reminder-settings-action-component.component';
import { EditReminderModalComponent } from "./components/edit-reminder-modal/edit-reminder-modal.component";

@Component({
  selector: 'app-reminders-page',
  standalone: true,
  imports: [
    LoaderComponent,
    ListGridClientSideComponent
  ],
  templateUrl: 'reminders-page.component.html',
  styleUrls: ['reminders-page.component.scss', '../../styles/settings-styles.scss']
})
export class RemindersPageComponent extends CommonPageComponent implements OnInit {
  settingsApiService = inject(SettingsApiService);
  modalService = inject(NgbModal);
  toastr = inject(ToastrService);

  override readonly headerData: IPartialHeaderData = {
    title: 'Reminders',
    iconClass: 'ic-bell-20',
    backPath: ['/settings']
  };

  editRemindersModalOpened = false;
  reminders: ReminderSettingsModelExtended[] = [];

  override ngOnInit(): void {
    super.ngOnInit();

    this.loadReminders();
    this.setupColDefs();
  }

  edit(reminder: ReminderSettingsModelExtended): void {
    if (this.editRemindersModalOpened) return;

    this.editRemindersModalOpened = true;

    const modalRef = this.modalService.open(EditReminderModalComponent, { size: 'sm', centered: true });
    const instance = modalRef.componentInstance as EditReminderModalComponent;

    instance.reminder = structuredClone(reminder);

    const biggerLimitsReminderTypes = [ReminderSettingTypeEnum.AnnualStatementPayment, ReminderSettingTypeEnum.AnnualStatementSigning];

    if (biggerLimitsReminderTypes.includes(reminder.type)) {
      instance.datesLimits.maxNumber = 60;
    }

    instance.confirm
      .pipe(take(1))
      .subscribe((data: ReminderSettingsModelExtended) => {
        this.editRemindersModalOpened = false;
        this.reminders = this.reminders.map((r) => {
          if (r.type === data.type) {
            return new ReminderSettingsModelExtended(data, data.type);
          }
          return r;
        });

        this.gridApi.refreshCells({ force: true });
      });

    void modalRef.result.finally(() => this.editRemindersModalOpened = false);
  }

  loadReminders(): void {
    this.isLoading.set(true);

    this.settingsApiService.getReminderSettings()
      .subscribe({
        next: (reminders: ReminderSettingsData) => {
          this.isLoading.set(false);

          this.reminders = [
            new ReminderSettingsModelExtended(reminders.annualStatementPaymentSetting, ReminderSettingTypeEnum.AnnualStatementPayment),
            new ReminderSettingsModelExtended(reminders.companyDebtSetting, ReminderSettingTypeEnum.CompanyDebt),
            new ReminderSettingsModelExtended(reminders.annualStatementSigningSetting, ReminderSettingTypeEnum.AnnualStatementSigning),
            new ReminderSettingsModelExtended(reminders.asicFormSigningSetting, ReminderSettingTypeEnum.AsicFormSigning)
          ];
        },
        error: (error) => {
          this.toastr.error(somethingWentWrong, 'Error');
          console.warn('[RemindersPageComponent.loadReminders] error', error);
        }
      });
  }

  // =====  Grid  =====

  readonly remindersRowSelection: RowSelectionOptions<any> = BaseListGridComponent.noCheckboxesRowSelection;

  colDefs: ColDef[] = [];
  gridApi!: GridApi;

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
  }

  setupColDefs(): void {
    this.colDefs = [
      {
        headerName: 'Reminders',
        field: 'type',
        sort: 'asc',
        flex: 4,
        valueFormatter: (params: ValueFormatterParams<ReminderSettingsModelExtended, ReminderSettingTypeEnum | undefined>) => ReminderNameTypePipe.formatValue(params.value),
        onCellClicked: (event: CellClickedEvent<ReminderSettingsModelExtended>) => this.edit(event.data!),
        cellClass: 'hover-underline cursor-pointer'
      },
      {
        headerName: 'Method of Sending',
        field: 'sendingType',
        width: 230,
        minWidth: 100,
        cellRenderer: AgReminderSendingMethodLabelComponent,
        flex: 2,
      },
      {
        headerName: 'Date of sending before the due date',
        field: 'dates',
        valueGetter: (params) => {
          const reminderSetting = (params.data as ReminderSettingModel);
          return (reminderSetting?.scheduleDaysBeforeDeadline ?? []).map((day) => `${ day } days`);
        },
        flex: 3,
      },
      {
        headerName: 'Time',
        field: 'reminderTime',
        width: 100,
        minWidth: 100,
        flex: 2,
      },
      {
        cellRenderer: AgReminderSettingsActionComponentComponent,
        cellRendererParams: {
          edit: this.edit.bind(this)
        },
        sortable: false,
        width: 60,
        minWidth: 60,
      }
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));
  }
}
